import {
  Alert,
  Button,
  Container,
  FormControl,
  Grid,
  SxProps,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import { isValid } from "date-fns";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useFormattedTitleFromUser from "hooks/useFormattedTitleFromUser";
import { useMemo } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  OrganizationUserDetails,
  ResponseCode,
  ResponseOfSkus,
  ResponseOfUserSkuExtended,
  UserChargebackRequest,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { convertToUTC, standardDateFormat } from "utils/dateUtils";
import translateError from "utils/translateError";
import { ProcessChargebackDialog } from "./ProcessChargeBackDialog";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  deleteUserSku: {
    margin: "30px 0",
  },
  userSkuInfo: {
    mb: theme.spacing(2),
  },
  confirmationMessageBlock: {
    margin: "15px 0",
  },
});

type UserChargebackFormValues = Omit<
  UserChargebackRequest,
  "userSkuId" | "init" | "toJSON"
>;

const { Form } = withTypes<UserChargebackFormValues>();

const UserChargebackPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();

  const [
    chargeBackCofirmationOpen,
    chargeBackCofirmationOpenActions,
  ] = useBoolean(false);

  const {
    organizationUserService,
    userSkuService,
    skuService,
    userCourseUrl,
  } = useGlobalContext();

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  useFormattedTitleFromUser("processChargeBackTitle", user, t({ id: "user" }));

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { enabled: !!userResult && !userResult.isLoading }
  );

  const userSku = userSkuResult?.data?.data!;

  const skuResult = useQuery<ResponseOfSkus>(
    ["sku", userSku?.skuId],
    () => skuService.getSkuById(userSku?.skuId!),
    {
      enabled: !!userSku && !userSkuResult.isLoading,
    }
  );

  const sku = skuResult?.data?.data!;

  const loading =
    userResult.isLoading || userSkuResult.isLoading || skuResult.isLoading;

  const rows = [
    RowElement(
      "userNameLabel",
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
      loading
    ),
    RowElement("emailLabel", user?.email!, userResult.isLoading),
    RowElement("userId", user?.id!, userResult.isLoading),
    RowElement("courseName", userSku?.title ?? "", userSkuResult.isLoading),
    RowElement("skuLabel", sku?.sku ?? "", skuResult.isLoading),
    RowElement("userSkuId", userSkuId!),
    RowElement("startDateLabel", userSku?.startDate!, userSkuResult.isLoading),
    RowElement("endDateLabel", userSku?.endDate!, userSkuResult.isLoading),
    RowElement(
      "statusLabel",
      userSku?.userSkuStateName!,
      userSkuResult.isLoading
    ),
  ].filter((r) => !!r.component);

  const init = useMemo<UserChargebackRequest>(() => {
    return UserChargebackRequest.fromJS({
      userSkuId,
    });
  }, [userSkuId]);

  const onProcessUserChargeback = async (values: UserChargebackFormValues) => {
    try {
      const result = await userSkuService.processUserChargeback(
        UserChargebackRequest.fromJS({
          ...values,
          userId: user?.id,
          organizationId: user?.organizationId,
          userSkuId,
          renewalDate: convertToUTC(values.renewalDate),
        })
      );

      if (result.responseCode === ResponseCode.OK) {
        queryClient.invalidateQueries(["getUserSku", userSkuId]);
        queryClient.invalidateQueries(["userSkus"]);
        persistanceStore.update((s) => {
          s.userSkusFilterChargebackRenewalDate = undefined;
        });

        chargeBackCofirmationOpenActions.setFalse();
      }
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />

      <h1>{t({ id: "processChargeBackLabel" })}</h1>

      <EntityDetails
        title={t({ id: "registrationDetails" })}
        details={rows}
        sx={styles.userSkuInfo}
      />
      <Form
        onSubmit={onProcessUserChargeback}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const saveButtonEnabled =
            !userSkuResult.isFetching &&
            !userSkuResult.isLoading &&
            values.renewalDate != null;

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2} mr={2} alignItems="center">
                <Grid item>
                  <Field name="renewalDate">
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DatePicker
                          disabled={!userSkuResult.isFetched}
                          label={t({ id: "renewalDateLabel" })}
                          format={standardDateFormat}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={submitting || !saveButtonEnabled}
                    onClick={() => {
                      persistanceStore.update((s) => {
                        s.userSkusFilterChargebackRenewalDate =
                          isValid(values.renewalDate)
                            ? convertToUTC(values.renewalDate)
                            : undefined;
                      });

                      chargeBackCofirmationOpenActions.setTrue();
                    }}
                  >
                    <T id="processChargeBackLabel" />
                  </Button>
                </Grid>
              </Grid>

              <ProcessChargebackDialog
                onClose={chargeBackCofirmationOpenActions.setFalse}
                open={chargeBackCofirmationOpen}
                submitting={submitting}
                saveButtonEnabled={saveButtonEnabled}
                user={user!}
                parentUserSkuId={userSkuId!}
              />

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </form>
          );
        }}
      />
    </Container>
  );
};

export default UserChargebackPage;
